import React, { useState, useEffect, useRef } from 'react';
import Dashboard from './components/Dashboard';
import RequestList from './components/RequestList';
import RequestDetail from './components/RequestDetail';
import SubmitRequest from './components/SubmitRequest';
import PromptTester from './components/PromptTester';
import BatchList from './components/BatchList';
import ExtractionList from './components/ExtractionList';
import Login from './components/Login';
import SharedExpenditure from './components/SharedExpenditure';
import SharedBatch from './components/SharedBatch';
import SharedRequest from './components/SharedRequest';
import Config from './components/Config';
import DataViewer from './components/DataViewer';
import { getMe } from './services/api';

// Primary tabs always visible in the header
const PRIMARY_TABS = ['Batches', 'Extraction', 'API Requests', 'Config'];
// Secondary tabs grouped under the Tools dropdown
const TOOLS_TABS   = ['Submit Request', 'Data Viewer', 'Prompt Tester'];

function ToolsDropdown({ activeTab, onSelect }) {
  const [open, setOpen] = useState(false);
  const ref    = useRef(null);
  const btnRef = useRef(null);
  const isToolActive = TOOLS_TABS.includes(activeTab);

  // Close on outside click
  useEffect(() => {
    function close(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  // Keyboard: toggle button
  const handleBtnKeyDown = (e) => {
    if (e.key === 'Escape') { setOpen(false); return; }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      // focus first menu item on next render tick
      setTimeout(() => ref.current?.querySelector('[role="menuitem"]')?.focus(), 0);
    }
  };

  // Keyboard: inside the menu
  const handleMenuKeyDown = (e) => {
    const items = [...(ref.current?.querySelectorAll('[role="menuitem"]') || [])];
    const idx   = items.indexOf(document.activeElement);
    if (e.key === 'ArrowDown')  { e.preventDefault(); items[(idx + 1) % items.length]?.focus(); }
    if (e.key === 'ArrowUp')    { e.preventDefault(); items[(idx - 1 + items.length) % items.length]?.focus(); }
    if (e.key === 'Escape')     { setOpen(false); btnRef.current?.focus(); }
    if (e.key === 'Tab')        { setOpen(false); }
    if (e.key === 'Home')       { e.preventDefault(); items[0]?.focus(); }
    if (e.key === 'End')        { e.preventDefault(); items[items.length - 1]?.focus(); }
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        ref={btnRef}
        className={`tab-btn ${isToolActive ? 'active' : ''}`}
        onClick={() => setOpen(o => !o)}
        onKeyDown={handleBtnKeyDown}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={isToolActive ? `Tools: ${activeTab} (menu)` : 'Tools menu'}
        style={{ display: 'flex', alignItems: 'center', gap: 5 }}
      >
        {isToolActive ? activeTab : 'Tools'}
        <span aria-hidden="true" style={{ fontSize: 9, opacity: 0.7, marginTop: 1 }}>▾</span>
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Tools"
          onKeyDown={handleMenuKeyDown}
          style={{
            position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 300,
            background: 'var(--bg-secondary)', border: '1px solid var(--border)',
            borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
            minWidth: 180, overflow: 'hidden',
          }}
        >
          {TOOLS_TABS.map(tab => (
            <button
              key={tab}
              role="menuitem"
              onClick={() => { onSelect(tab); setOpen(false); btnRef.current?.focus(); }}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: '10px 16px', fontSize: 13, border: 'none', cursor: 'pointer',
                background: activeTab === tab ? 'var(--accent)' : 'transparent',
                color: activeTab === tab ? '#fff' : 'var(--text-primary)',
                fontWeight: activeTab === tab ? 600 : 400,
              }}
              onMouseEnter={e => { if (activeTab !== tab) { e.currentTarget.style.background = 'rgba(140,21,21,0.10)'; e.currentTarget.style.color = '#8C1515'; } }}
              onMouseLeave={e => { if (activeTab !== tab) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-primary)'; } }}
            >
              {tab}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Parse share params from URL query string.
 * Supports formats:
 *   ?view=share&req=<requestId>&exp=<expId>   → MatchRequest-based single expenditure share
 *   ?view=share&req=<requestId>               → MatchRequest all-results share
 *   ?view=share&batch=<batchId>&exp=<expId>   → Batch-based single expenditure share
 *   ?view=share&batch=<batchId>               → Batch all-results share
 */
function getShareParams() {
  const params = new URLSearchParams(window.location.search);
  if (params.get('view') === 'share') {
    const exp   = params.get('exp');
    const req   = params.get('req');
    const batch = params.get('batch');
    if (exp && req)   return { requestId: req,   expId: exp };
    if (exp && batch) return { batchId: batch,   expId: exp };
    if (batch)        return { batchId: batch,   expId: null };
    if (req)          return { requestId: req,   expId: null };
  }
  return null;
}

function getInitialTheme() {
  const saved = localStorage.getItem('theme');
  if (saved) return saved;
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

export default function App() {
  const shareParams = getShareParams();
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [activeTab, setActiveTab] = useState('Batches');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [modeFilter, setModeFilter] = useState('');
  const [theme, setTheme] = useState(getInitialTheme);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { setAuthChecked(true); return; }
    getMe()
      .then(u => setUser(u))
      .catch(() => { localStorage.removeItem('token'); localStorage.removeItem('user'); })
      .finally(() => setAuthChecked(true));
  }, []);

  const toggleTheme = () => setTheme(t => t === 'dark' ? 'light' : 'dark');

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  if (shareParams && shareParams.expId) {
    return (
      <SharedExpenditure
        requestId={shareParams.requestId || null}
        batchId={shareParams.batchId || null}
        expId={shareParams.expId}
      />
    );
  }
  if (shareParams && shareParams.batchId && !shareParams.expId) {
    return <SharedBatch batchId={shareParams.batchId} />;
  }
  if (shareParams && shareParams.requestId && !shareParams.expId) {
    return <SharedRequest requestId={shareParams.requestId} />;
  }

  if (!authChecked) {
    return (
      <div className="processing-indicator" role="status" aria-live="polite">
        <div className="spinner" aria-hidden="true" />
        <span>Loading…</span>
      </div>
    );
  }

  if (!user) {
    return <Login onLogin={setUser} />;
  }

  return (
    <>
      {/* Skip navigation for keyboard/screen-reader users */}
      <a href="#main-content" className="skip-link">Skip to main content</a>

      <div className="app-shell">
        <header className="app-header" role="banner">
          <div className="app-logo">
            <div className="app-logo-icon" aria-hidden="true">S</div>
            <div>
              <h1>Gift Award Matcher</h1>
              <span>Stanford University &mdash; AI-powered gift fund matching</span>
            </div>
          </div>

          {/* Centered navigation */}
          <nav className="header-nav" aria-label="Main navigation">
            <div className="tab-bar" role="tablist">
              {PRIMARY_TABS.map(tab => (
                <button
                  key={tab}
                  role="tab"
                  aria-selected={activeTab === tab}
                  className={`tab-btn ${activeTab === tab ? 'active' : ''}`}
                  onClick={() => { setActiveTab(tab); setSelectedRequest(null); }}
                >
                  {tab}
                </button>
              ))}
              <ToolsDropdown
                activeTab={activeTab}
                onSelect={tab => { setActiveTab(tab); setSelectedRequest(null); }}
              />
            </div>
          </nav>

          {/* Right-aligned controls */}
          <div className="header-controls">
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }} aria-label={`Signed in as ${user.display_name || user.username}`}>
              {user.display_name || user.username}
            </span>
            <button
              className="theme-toggle"
              onClick={toggleTheme}
              aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
              title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
            >
              <span className="theme-toggle-icon" aria-hidden="true">{theme === 'dark' ? '\u2600' : '\u263D'}</span>
              {theme === 'dark' ? 'Light' : 'Dark'}
            </button>
            <button
              className="theme-toggle"
              onClick={handleLogout}
              aria-label="Sign out"
              title="Sign out"
            >
              Logout
            </button>
          </div>
        </header>

        <main id="main-content" className="app-content">
          {activeTab === 'API Requests' && !selectedRequest && (
            <>
              <Dashboard />
              <RequestList onSelect={setSelectedRequest} modeFilter={modeFilter} onModeChange={setModeFilter} />
            </>
          )}

          {activeTab === 'API Requests' && selectedRequest && (
            <RequestDetail requestId={selectedRequest} onBack={() => setSelectedRequest(null)} />
          )}

          {activeTab === 'Batches' && <BatchList />}

          {activeTab === 'Extraction' && <ExtractionList />}

          {activeTab === 'Data Viewer' && <DataViewer />}

          {activeTab === 'Submit Request' && (
            <SubmitRequest onSubmitted={(id) => { setSelectedRequest(id); setActiveTab('API Requests'); }} />
          )}

          {activeTab === 'Prompt Tester' && <PromptTester />}

          {activeTab === 'Config' && <Config />}
        </main>
      </div>
    </>
  );
}
